import type {
  SnapEndpointDiscovery,
  SnapError,
  SnapSubmitResult,
  SnapTenantSolutionContext
} from '../types';
import { SnapDenyCodes } from '../types';
import { createSnapError } from '../security/sanitize';
import { mapCustomerError } from '../security/customerErrors';

export interface SubmitArgs {
  apiBaseUrl: string;
  endpoints: SnapEndpointDiscovery;
  authorization: string;
  correlationId: string;
  title: string;
  description: string;
  priority?: number;
  categoryId?: number | null;
  contextJson: string;
  metadataJson?: string;
  screenshot?: Blob | null;
  attachments: File[];
  fetchImpl: typeof fetch;
  signal?: AbortSignal;
  /** Server context is sent as hints only; server remains authoritative. */
  serverContext: SnapTenantSolutionContext;
}

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

function mapDenial(
  body: Record<string, unknown>,
  correlationId: string,
  httpStatus?: number
): SnapError {
  const code = String(body.code ?? body.Code ?? SnapDenyCodes.Unauthorized);
  const message = String(
    body.message ?? body.Message ?? 'Snap submission was denied.'
  );
  if (/please log in/i.test(message)) {
    return mapCustomerError({ code, message, httpStatus, correlationId });
  }
  const expired =
    code === SnapDenyCodes.BootstrapExpired ||
    /expired/i.test(code) ||
    /expired/i.test(message);
  return createSnapError(
    expired ? 'bootstrap_expired' : 'submission_denied',
    code,
    message,
    { correlationId, retryable: expired, userSafe: true }
  );
}

/**
 * Submit via approved Integration Contract ticket-create path.
 * Attaches short-lived bootstrap authorization (header + form field).
 * Does not use AppKey. Does not call Panel BFF routes.
 */
export async function submitSupportRequest(args: SubmitArgs): Promise<SnapSubmitResult> {
  if (args.signal?.aborted) {
    return {
      success: false,
      correlationId: args.correlationId,
      error: createSnapError(
        'cancelled',
        SnapDenyCodes.Cancelled,
        'Submission cancelled.',
        { correlationId: args.correlationId }
      )
    };
  }

  const url = joinUrl(args.apiBaseUrl, args.endpoints.ticketCreatePath);
  const form = new FormData();
  form.append('Title', args.title);
  form.append('Description', args.description);
  form.append('Priority', String(args.priority ?? 3));
  if (args.categoryId != null) form.append('CategoryId', String(args.categoryId));
  form.append('ClientCorrelationId', args.correlationId);
  form.append('ClientCapturedUtc', new Date().toISOString());
  form.append('CaptureMode', args.screenshot ? 'SnapScreen' : 'SnapBrowser');
  form.append('SnapBootstrapAuthorization', args.authorization);
  form.append('ClientRoute', safeRouteHint(args.contextJson));
  if (args.metadataJson) {
    form.append('SnapScreenMetadataJson', args.metadataJson);
  }

  // SolutionUid is a client hint only — server ignores for authorization.
  if (args.serverContext.solutionUid) {
    form.append('SolutionUid', args.serverContext.solutionUid);
  }

  // ASP.NET Core form binding is case-insensitive; use Panel casing once.
  if (args.screenshot) {
    form.append(
      'Attachments',
      args.screenshot,
      `snap-screen-${Date.now()}.png`
    );
  }
  for (const file of args.attachments) {
    form.append('Attachments', file, file.name);
  }

  const headers: Record<string, string> = {
    [args.endpoints.correlationHeaderName]: args.correlationId,
    [args.endpoints.bootstrapHeaderName]: args.authorization
  };

  let response: Response;
  try {
    response = await args.fetchImpl(url, {
      method: 'POST',
      headers,
      body: form,
      // PR-8.7C.1 — bootstrap header only; never attach Panel cookies.
      credentials: 'omit',
      signal: args.signal
    });
  } catch (err) {
    if (args.signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
      return {
        success: false,
        correlationId: args.correlationId,
        error: createSnapError(
          'cancelled',
          SnapDenyCodes.Cancelled,
          'Submission cancelled.',
          { correlationId: args.correlationId }
        )
      };
    }
    return {
      success: false,
      correlationId: args.correlationId,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        'Network transport failed while submitting the support request.',
        { correlationId: args.correlationId, retryable: true, userSafe: true }
      )
    };
  }

  const responseCorrelation =
    response.headers.get(args.endpoints.correlationHeaderName) || args.correlationId;

  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    body = {};
  }

  if (response.status === 401 || response.status === 403) {
    return {
      success: false,
      correlationId: responseCorrelation,
      error: mapDenial(body, responseCorrelation, response.status)
    };
  }

  if (!response.ok) {
    const code = String(body.code ?? body.Code ?? '');
    if (code.startsWith('SNAP_') || /please log in/i.test(String(body.message ?? ''))) {
      return {
        success: false,
        correlationId: responseCorrelation,
        error: mapDenial(body, responseCorrelation, response.status)
      };
    }
    return {
      success: false,
      correlationId: responseCorrelation,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        'Support could not submit your request right now.',
        { correlationId: responseCorrelation, retryable: response.status >= 500, userSafe: true }
      )
    };
  }

  const success = body.success === true || body.Success === true || response.ok;
  return {
    success: !!success,
    ticketId: (body.ticketId ?? body.TicketId) as number | string | undefined,
    ticketNumber: (body.ticketNumber ?? body.TicketNumber) as string | undefined,
    correlationId: responseCorrelation
  };
}

function safeRouteHint(contextJson: string): string {
  try {
    const parsed = JSON.parse(contextJson) as {
      browserObserved?: { pageUrl?: string };
    };
    return (parsed.browserObserved?.pageUrl || '').slice(0, 500);
  } catch {
    return '';
  }
}

export async function authorizeCreate(args: {
  apiBaseUrl: string;
  endpoints: SnapEndpointDiscovery;
  authorization: string;
  correlationId: string;
  fetchImpl: typeof fetch;
  signal?: AbortSignal;
}): Promise<{ ok: true } | { ok: false; error: SnapError }> {
  const url = joinUrl(args.apiBaseUrl, args.endpoints.authorizePath);
  try {
    const response = await args.fetchImpl(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [args.endpoints.correlationHeaderName]: args.correlationId,
        [args.endpoints.bootstrapHeaderName]: args.authorization
      },
      body: JSON.stringify({
        authorization: args.authorization,
        correlationId: args.correlationId,
        operation: 'ticket.create'
      }),
      // Bootstrap JWT is the sole auth material — omit host cookies so Panel sessions
      // on shared domains cannot override external Snap requester identity (PR-8.7C.1).
      credentials: 'omit',
      signal: args.signal
    });

    const correlation =
      response.headers.get(args.endpoints.correlationHeaderName) || args.correlationId;
    let body: Record<string, unknown> = {};
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      body = {};
    }

    const createAllowed =
      body.createAllowed === true || body.CreateAllowed === true;
    const success = body.success === true || body.Success === true;

    if (!response.ok || !success || !createAllowed) {
      return { ok: false, error: mapDenial(body, correlation, response.status) };
    }
    return { ok: true };
  } catch (err) {
    if (args.signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
      return {
        ok: false,
        error: createSnapError(
          'cancelled',
          SnapDenyCodes.Cancelled,
          'Authorization cancelled.',
          { correlationId: args.correlationId }
        )
      };
    }
    return {
      ok: false,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        'Network transport failed during authorize.',
        { correlationId: args.correlationId, retryable: true }
      )
    };
  }
}
